import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { api, creditsAPI, isSessionExpired, SESSION_EXPIRED_SENTINEL } from '../services/api'
import { paymentService } from '../services/paymentService'

/** Handle thunk errors: return sentinel for session expiry, sanitize otherwise */
function handleError(error: any, fallback: string): string {
  if (isSessionExpired(error)) return SESSION_EXPIRED_SENTINEL
  return error?.response?.data?.message || error?.message || fallback
}

// Types
export interface User {
  userId: string
  email: string
  tier: string
  credits: number
  stripeCustomerId?: string
}

export interface CreditBalance {
  balance: number
  lifetime: number
  tier: string
  monthlyAllocation?: number
  rolloverLimit?: number
  nextRenewal?: string
}

export interface Subscription {
  tier: string
  tierName: string
  billingPeriod: 'MONTHLY' | 'ANNUAL' | null
  status: 'ACTIVE' | 'CANCELED' | 'PAST_DUE' | 'INCOMPLETE' | 'TRIALING' | null
  stripeSubscriptionId: string | null
  currentPeriodEnd: string | null
  startDate: string | null
  trialEnd: string | null // When the trial ends (null if not trialing)
  nextRenewal: string | null
  monthlyCredits: number
  rolloverLimit: number | null
  features: string[]
  isActive: boolean
  isPastDue: boolean
  isTrialing: boolean // True if subscription is in trial period
  requiresAction: boolean
  lastUpdated: string
  updatedAt: string
}

export interface UserStats {
  totalUnlocks: number
  creditsUsed: number
}

export interface Receipt {
  receiptId: string
  invoiceNumber: string
  transactionType: 'subscription' | 'credits' | 'one-time'
  description: string
  amount: number
  currency: string
  status: 'paid' | 'pending' | 'failed' | 'refunded'
  paymentMethod?: {
    type: string
    last4?: string
    brand?: string
  }
  billingDetails?: {
    name: string
    email: string
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      postalCode?: string
      country?: string
    }
  }
  items?: {
    description: string
    quantity: number
    unitPrice: number
    amount: number
  }[]
  stripeInvoiceId?: string
  stripePaymentIntentId?: string
  pdfUrl?: string
  createdAt: string
  paidAt?: string
}

export interface Invoice {
  id: string
  invoiceNumber: string
  transactionType: 'subscription' | 'one-time'
  description: string
  amount: number
  currency: string
  status: 'paid' | 'pending' | 'failed'
  paymentMethod?: {
    type: string
    last4?: string
    brand?: string
  }
  pdfUrl?: string        // Invoice PDF URL
  receiptPdfUrl?: string // Receipt PDF URL
  createdAt: string
  paidAt?: string
}

export interface Feedback {
  feedbackId: string
  type: 'feedback' | 'feature' | 'bug'
  subject: string
  message: string
  status: string
  priority?: string
  flagged?: boolean
  groupId?: string
  groupName?: string
  archived?: boolean
  createdAt: string
  updatedAt: string
}

interface UserState {
  user: User | null
  credits: CreditBalance | null
  subscription: Subscription | null
  receipts: Receipt[]
  invoices: Invoice[]
  feedbacks: Feedback[]
  features: string[]
  stats: UserStats | null
  loading: boolean
  error: string | null
}

const initialState: UserState = {
  user: null,
  credits: null,
  subscription: null,
  receipts: [],
  invoices: [],
  feedbacks: [],
  features: [],
  stats: null,
  loading: false,
  error: null
}

// Async Thunks

/**
 * Fetch user profile from API
 * GET /users/me
 */
export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/me')
      return {
        user: response.data.user,
        stats: response.data.stats
      }
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch user profile'))
    }
  }
)

/**
 * Update user profile
 * PATCH /users/me
 */
export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async (updates: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await api.patch('/users/me', updates)
      return response.data.user
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to update user profile'))
    }
  }
)

/**
 * Delete user account
 * DELETE /users/me
 */
export const deleteUserAccount = createAsyncThunk(
  'user/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete('/users/me')
      return true
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to delete account'))
    }
  }
)

/**
 * Fetch credit balance
 * GET /credits/balance
 */
export const fetchCredits = createAsyncThunk(
  'user/fetchCredits',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { user: UserState }

      // Return cached data if available and not forcing refresh
      if (!shouldRefresh && state.user.credits) {
        return {
          credits: state.user.credits,
          subscription: state.user.subscription,
          fromCache: true
        }
      }

      const response = await creditsAPI.getBalance()
      return {
        credits: response.credits,
        subscription: response.subscription,
        fromCache: false
      }
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch credits'))
    }
  }
)

/**
 * Initialize credits for new user
 * POST /credits/initialize
 */
export const initializeCredits = createAsyncThunk(
  'user/initializeCredits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await creditsAPI.initialize()
      return response.credits
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to initialize credits'))
    }
  }
)

/**
 * Fetch available features for user's tier
 * GET /credits/features
 */
export const fetchFeatures = createAsyncThunk(
  'user/fetchFeatures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await creditsAPI.getFeatures()
      return response.features || []
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch features'))
    }
  }
)

/**
 * Fetch subscription details
 * NOTE: Subscription is included in /credits/balance response
 * This thunk just returns cached data from fetchCredits()
 * GET /payments/subscription (deprecated - use fetchCredits instead)
 */
export const fetchSubscription = createAsyncThunk(
  'user/fetchSubscription',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { user: UserState }

      // Return cached subscription if available and not forcing refresh
      if (!shouldRefresh && state.user.subscription) {
        return {
          subscription: state.user.subscription,
          fromCache: true
        }
      }

      // If no cached subscription, it means fetchCredits hasn't been called yet
      // Fetch subscription via the balance API (which returns both credits and subscription)
      const response = await paymentService.getSubscription()
      return {
        subscription: response.subscription,
        fromCache: false
      }
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch subscription'))
    }
  }
)

/**
 * Fetch user stats (unlocks, credits used, etc.)
 */
export const fetchUserStats = createAsyncThunk(
  'user/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/me/stats')
      return response.data.stats
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch user stats'))
    }
  }
)

/**
 * Fetch payment receipts and invoices
 * GET /payments/receipts
 */
export const fetchReceipts = createAsyncThunk(
  'user/fetchReceipts',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { user: UserState }

      // Return cached data if available and not forcing refresh
      if (!shouldRefresh && state.user.receipts.length > 0) {
        return {
          receipts: state.user.receipts,
          invoices: state.user.invoices,
          fromCache: true
        }
      }

      const response = await paymentService.getReceipts()
      return {
        receipts: response.receipts || [],
        invoices: response.invoices || [],
        fromCache: false
      }
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch receipts'))
    }
  }
)

// Submit feedback
export const submitFeedback = createAsyncThunk(
  'user/submitFeedback',
  async (params: { type: 'feedback' | 'feature' | 'bug', subject: string, message: string }, { rejectWithValue }) => {
    try {
      const { feedbackAPI } = await import('../services/api')
      await feedbackAPI.submit(params.type, params.subject, params.message)
      return { success: true }
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to submit feedback'))
    }
  }
)

// Fetch user's feedbacks
export const fetchMyFeedback = createAsyncThunk(
  'user/fetchMyFeedback',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { user: UserState }

      // Return cached data if available and not forcing refresh
      if (!shouldRefresh && state.user.feedbacks.length > 0) {
        return {
          feedbacks: state.user.feedbacks,
          fromCache: true
        }
      }

      const { feedbackAPI } = await import('../services/api')
      const response = await feedbackAPI.getMyFeedback()
      return {
        feedbacks: response.feedbacks || [],
        fromCache: false
      }
    } catch (error: any) {
      return rejectWithValue(handleError(error, 'Failed to fetch feedbacks'))
    }
  }
)

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Set user data directly (e.g., from login)
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.error = null
    },

    // Update credits after unlock/purchase
    updateCredits: (state, action: PayloadAction<number>) => {
      if (state.user) {
        state.user.credits = action.payload
      }
      if (state.credits) {
        state.credits.balance = action.payload
      }
    },

    // Update user tier
    updateTier: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.tier = action.payload
      }
      if (state.credits) {
        state.credits.tier = action.payload
      }
    },

    // Clear user state (on logout)
    clearUser: (state) => {
      state.user = null
      state.credits = null
      state.subscription = null
      state.receipts = []
      state.invoices = []
      state.feedbacks = []
      state.features = []
      state.stats = null
      state.error = null
    },

    // Clear error
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    // Fetch user profile
    builder.addCase(fetchUserProfile.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.loading = false
      state.user = action.payload.user
      state.stats = action.payload.stats
      state.error = null
    })
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Update user profile
    builder.addCase(updateUserProfile.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateUserProfile.fulfilled, (state, action) => {
      state.loading = false
      state.user = action.payload
      state.error = null
    })
    builder.addCase(updateUserProfile.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Delete user account
    builder.addCase(deleteUserAccount.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(deleteUserAccount.fulfilled, (state) => {
      state.loading = false
      state.user = null
      state.credits = null
      state.subscription = null
      state.features = []
      state.stats = null
      state.error = null
    })
    builder.addCase(deleteUserAccount.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Fetch credits
    builder.addCase(fetchCredits.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchCredits.fulfilled, (state, action) => {
      state.loading = false
      state.credits = action.payload.credits
      // CRITICAL: Sync user.credits with the real-time value from DynamoDB
      if (state.user && action.payload.credits) {
        state.user.credits = action.payload.credits.balance
        state.user.tier = action.payload.credits.tier
      }
      if (action.payload.subscription) {
        state.subscription = action.payload.subscription
      }
      state.error = null
    })
    builder.addCase(fetchCredits.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Initialize credits
    builder.addCase(initializeCredits.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(initializeCredits.fulfilled, (state, action) => {
      state.loading = false
      state.credits = action.payload
      state.error = null
    })
    builder.addCase(initializeCredits.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Fetch features
    builder.addCase(fetchFeatures.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchFeatures.fulfilled, (state, action) => {
      state.loading = false
      state.features = action.payload
      state.error = null
    })
    builder.addCase(fetchFeatures.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Fetch subscription
    builder.addCase(fetchSubscription.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchSubscription.fulfilled, (state, action) => {
      state.loading = false
      // Handle both old format (direct subscription) and new format (with fromCache)
      state.subscription = (action.payload as any).subscription || action.payload
      state.error = null
    })
    builder.addCase(fetchSubscription.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Fetch user stats
    builder.addCase(fetchUserStats.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchUserStats.fulfilled, (state, action) => {
      state.loading = false
      state.stats = action.payload
      state.error = null
    })
    builder.addCase(fetchUserStats.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Fetch receipts and invoices
    builder.addCase(fetchReceipts.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchReceipts.fulfilled, (state, action) => {
      state.loading = false
      // Handle both old format (direct array) and new format (with fromCache)
      state.receipts = (action.payload as any).receipts || action.payload
      state.invoices = (action.payload as any).invoices || []
      state.error = null
    })
    builder.addCase(fetchReceipts.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })

    // Fetch my feedbacks
    builder.addCase(fetchMyFeedback.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchMyFeedback.fulfilled, (state, action) => {
      state.loading = false
      state.feedbacks = (action.payload as any).feedbacks || action.payload
      state.error = null
    })
    builder.addCase(fetchMyFeedback.rejected, (state, action) => {
      state.loading = false
      if ((action.payload as string) !== SESSION_EXPIRED_SENTINEL) state.error = action.payload as string
    })
  }
})

// Actions
export const { setUser, updateCredits, updateTier, clearUser, clearError } = userSlice.actions

// Selectors
export const selectUser = (state: { user: UserState }) => state.user.user
export const selectCredits = (state: { user: UserState }) => state.user.credits
export const selectSubscription = (state: { user: UserState }) => state.user.subscription
export const selectReceipts = (state: { user: UserState }) => state.user.receipts
export const selectInvoices = (state: { user: UserState }) => state.user.invoices
export const selectFeedbacks = (state: { user: UserState }) => state.user.feedbacks
export const selectFeatures = (state: { user: UserState }) => state.user.features
export const selectUserStats = (state: { user: UserState }) => state.user.stats
export const selectUserLoading = (state: { user: UserState }) => state.user.loading
export const selectUserError = (state: { user: UserState }) => state.user.error

// Helper selectors
export const selectIsUnlimitedTier = (state: { user: UserState }) => {
  const tier = state.user.user?.tier || state.user.credits?.tier
  return tier === 'UNLIMITED'
}

export const selectCanUnlock = (state: { user: UserState; features?: { featureAccess?: { isLaunchPromo?: boolean } | null } }) => {
  // Launch promo: all unlocks are free
  if (state.features?.featureAccess?.isLaunchPromo) return true

  const isUnlimited = selectIsUnlimitedTier(state)
  if (isUnlimited) return true

  const credits = state.user.credits?.balance || state.user.user?.credits || 0
  return credits > 0
}

/**
 * Get the current credit balance from the single source of truth
 * Priority: credits.balance (DynamoDB, real-time) > user.credits (Cognito token, stale)
 */
export const selectCreditBalance = (state: { user: UserState }): number => {
  // If we have fetched credits from DynamoDB, use that (real-time)
  if (state.user.credits !== null) {
    return state.user.credits.balance
  }
  // Otherwise, use Cognito token value as temporary fallback
  return state.user.user?.credits ?? 0
}

/**
 * Get the current tier from the single source of truth
 * Priority: credits.tier (DynamoDB, real-time) > user.tier (Cognito token, stale)
 */
export const selectCurrentTier = (state: { user: UserState }): string => {
  // If we have fetched credits from DynamoDB, use that tier (real-time)
  if (state.user.credits !== null) {
    return state.user.credits.tier
  }
  // Otherwise, use Cognito token tier as temporary fallback
  return state.user.user?.tier ?? 'STARTER'
}

/**
 * Get the subscription start date (for subscription trial calculation)
 */
export const selectSubscriptionStartDate = (state: { user: UserState }): string | null => {
  return state.user.subscription?.startDate ?? null
}

/**
 * Check if user is in subscription trial period (paid tier users get ALL features for 7 days)
 * This checks both the Stripe trial (status = TRIALING) and the feature preview trial
 */
export const selectIsInSubscriptionTrial = (state: { user: UserState }): boolean => {
  const subscription = state.user.subscription
  const tier = selectCurrentTier(state)
  const paidTiers = ['PRO', 'ELITE', 'UNLIMITED']

  // Only paid tiers qualify for subscription trial
  if (!paidTiers.includes(tier)) {
    return false
  }

  // Check if Stripe subscription is in trial status
  if (subscription?.status === 'TRIALING' || subscription?.isTrialing) {
    return true
  }

  // Must have a subscription start date for feature preview trial
  if (!subscription?.startDate) {
    return false
  }

  const startDate = new Date(subscription.startDate)
  const now = new Date()
  const trialDays = 7
  const daysSinceSubscription = Math.floor(
    (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  )

  return daysSinceSubscription < trialDays
}

/**
 * Check if user has a Stripe free trial (not charged yet)
 */
export const selectIsStripeTrialing = (state: { user: UserState }): boolean => {
  const subscription = state.user.subscription
  return subscription?.status === 'TRIALING' || subscription?.isTrialing === true
}

/**
 * Get trial end date (from Stripe trial or calculated from start date)
 */
export const selectTrialEndDate = (state: { user: UserState }): string | null => {
  const subscription = state.user.subscription

  // Use Stripe trial end if available
  if (subscription?.trialEnd) {
    return subscription.trialEnd
  }

  // Calculate from start date
  if (subscription?.startDate) {
    const startDate = new Date(subscription.startDate)
    const endDate = new Date(startDate)
    endDate.setDate(endDate.getDate() + 7)
    return endDate.toISOString()
  }

  return null
}

/**
 * Get remaining subscription trial days (returns 0 if not in trial)
 */
export const selectRemainingSubscriptionTrialDays = (state: { user: UserState }): number => {
  const subscription = state.user.subscription
  const tier = selectCurrentTier(state)
  const paidTiers = ['PRO', 'ELITE', 'UNLIMITED']

  // Only paid tiers qualify for subscription trial
  if (!paidTiers.includes(tier) || !subscription?.startDate) {
    return 0
  }

  const startDate = new Date(subscription.startDate)
  const trialDays = 7
  const trialEndDate = new Date(startDate)
  trialEndDate.setDate(trialEndDate.getDate() + trialDays)

  const now = new Date()
  const remainingMs = trialEndDate.getTime() - now.getTime()

  return Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)))
}

export default userSlice.reducer
